import React, { useCallback } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Link, Text
} from 'components/ui';
import RewardsCarousel from 'components/Content/BeautyInsider/RewardsCarousel';
import BiRewardButton from 'components/Content/BeautyInsider/BiRewardButton';
import anaConstants from 'analytics/constants';

/**
 * Displays a carousel of birthday gift options for Beauty Insider members.
 * Renders a personalized greeting, a "Choose Your Gift" heading with a "View All" link,
 * an optional days-to-redeem message, and a scrollable rewards carousel.
 * Returns null if no birthday gifts are available.
 *
 * @param {object} props
 * @param {object[]} props.birthdayGifts - List of birthday gift SKUs from the BI reward groups
 * @param {string} props.firstName - The user's first name for the personalized greeting
 * @param {object} props.basket - Current basket state passed down to BiRewardButton
 * @param {boolean} props.isAnonymous - Whether the user is not logged in
 * @param {object} props.localization - Localized strings
 * @param {string} props.localization.happyBday - "Happy Birthday" greeting text
 * @param {string} props.localization.chooseGift - "Choose Your Gift" heading text
 * @param {string|null} props.localization.daysToRedeemMsg - HTML string with days-to-redeem info, or null
 * @param {string} props.localization.viewAll - "View All" link text
 * @returns {React.ReactElement|null} The birthday gifts section, or null if no gifts are available
 */
const BeautyInsiderBirthdayGifts = props => {
    const {
        birthdayGifts, firstName, basket, isAnonymous, localization
    } = props;

    const renderBiButton = useCallback(
        ({ analyticsContext, sku }) => (
            <BiRewardButton
                sku={sku}
                analyticsContext={analyticsContext}
                isAnonymous={isAnonymous}
                basket={basket}
            />
        ),
        [isAnonymous, basket]
    );

    if (!birthdayGifts || birthdayGifts.length === 0) {
        return null;
    }

    const items = birthdayGifts.map(item => ({
        ...item,
        listPrice: 'birthday gift'
    }));

    return (
        <Box marginTop={[6, null, 7]}>
            <Box
                display='flex'
                flexDirection='column'
                gap='2px'
                marginBottom={4}
            >
                <Text
                    is='h3'
                    color='gray'
                    fontSize={['base', 'md']}
                    fontWeight='bold'
                    children={`${localization.happyBday}, ${firstName}!`}
                />
                <Flex
                    justifyContent='space-between'
                    alignItems='center'
                >
                    <Text
                        is='h2'
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        children={localization.chooseGift}
                    />
                    <Link
                        padding={2}
                        margin={-2}
                        color='blue'
                        target='_blank'
                        href='/beauty/birthday-gift'
                        children={localization.viewAll}
                    />
                </Flex>
                {/* Safe: daysToRedeemMsg is either a static locale string (FR) or a server-generated
                    field from the authenticated user's BI account (EN). It is not user-editable input. */}
                {localization.daysToRedeemMsg && (
                    <Box
                        fontSize='base'
                        data-at={Sephora.debug.dataAt('birthday_gifts_days_to_redeem')}
                        dangerouslySetInnerHTML={{ __html: localization.daysToRedeemMsg }}
                    />
                )}
            </Box>
            <RewardsCarousel
                paddingY={4}
                items={items}
                title={localization.chooseGift}
                showPrice={true}
                showMarketingFlags={true}
                hideVariationText={true}
                size='small'
                analyticsContext={anaConstants.CONTEXT.BASKET_REWARDS}
                renderBiButton={renderBiButton}
            />
        </Box>
    );
};

export default wrapFunctionalComponent(BeautyInsiderBirthdayGifts, 'BeautyInsiderBirthdayGifts');
